import {
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import FromSection from "../../components/FromSection";
import SubjectSection from "../../components/SubjectSection";
import RecipientsSection from "../../components/RecipientSection/RecipientsSection";
import DesignSection from "../../components/DesignSection";
import DesignEmail from "../../components/DesignEmail";
import { useContext, useState } from "react";
import { CampaignContext } from "../../Context";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { cloneDeep } from "lodash";
import {
  convertESTtoUserLocalDateAndTime,
  handleErrorMessage,
  isTruthy,
} from "helpers/methods";
import Schedule from "../../components/Schedule";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import TestModal from "../../components/TestModal";
import AttachmentSection from "../../components/AttachmentSection";
import {
  submitRegularCampaign,
  updateRegularCampaign,
} from "screens/Campaigns/CampaignService";
import ScheduleSection from "../../components/ScheduleSection";
import moment from "moment";

const Step2 = () => {
  const [isDesignEmailModalOpen, setIsDesignEmailModalOpen] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const { submitCampaign, setSubmitCampaign, saveAsDraft, managingStep } =
    useContext(CampaignContext);
  const [editName, setEditName] = useState(false);
  const [formFields, setFormFields] = useState({
    name: {
      value: submitCampaign.campaign.name ?? "",
      error: "",
    },
  });
  const [design, setDesign] = useState({
    content: submitCampaign.campaign.content ?? "",
    contentPlain: "",
    contentJson: submitCampaign.campaign.contentJson ?? {},
  });
  const [schedule, setSchedule] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const scheduleTime = moment(submitCampaign?.campaign?.scheduleTime).format(
    "MM/DD/YYYY HH:mm:ss"
  );

  const checkMessageIdForUpdateData =
    !isTruthy(submitCampaign?.campaignData?.messageId) &&
    submitCampaign.campaign?.status === "Submitted" &&
    moment(
      convertESTtoUserLocalDateAndTime(
        scheduleTime,
        "MM/DD/YYYY HH:mm:ss",
        "MM/DD/YYYY HH:mm:ss"
      )
    ).isAfter(moment().format("MM/DD/YYYY HH:mm:ss"));

  const saveCampaignName = async () => {
    let error = false;
    const clonedFormFields = cloneDeep(formFields);
    if (!isTruthy(clonedFormFields.name.value)) {
      error = true;
      clonedFormFields.name.error = "Please enter the campaign name";
    }
    if (error) {
      return setFormFields(clonedFormFields);
    }
    const updatedCampaign = {
      ...submitCampaign,
      campaign: {
        ...submitCampaign.campaign,
        name: formFields.name.value.trim(),
      },
    };
    setSubmitCampaign(updatedCampaign);
    setSaving(true);
    setEditName(false);
    await saveAsDraft(updatedCampaign);
    setSaving(false);
  };

  const handleSubmission = async (submitCampaign: any, scheduleTime: any) => {
    try {
      setSaving(true);
      if (checkMessageIdForUpdateData) {
        await updateRegularCampaign(submitCampaign, scheduleTime);
      } else {
        await submitRegularCampaign(submitCampaign, scheduleTime);
      }
      setSaving(false);
    } catch (error: any) {
      setSaving(false);
      throw error;
    }
  };

  const getHeader = () => {
    return (
      <Grid container gap={1} justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Back to campaigns">
            <IconButton
              onClick={() =>
                history.push(
                  `${
                    viewpaths.campaignsViewPath
                  }?page=${1}&rowsPerPage=${10}&status=All&category=all&currentTab=email`
                )
              }
            >
              <SvgIcon>
                <KeyboardBackspaceIcon fontSize="small" />
              </SvgIcon>
            </IconButton>
          </Tooltip>
          {editName ? (
            <TextField
              size="small"
              variant="outlined"
              placeholder="Campaign Name"
              value={formFields.name.value}
              onChange={(event: any) => {
                setFormFields({
                  ...formFields,
                  name: {
                    value: event.target.value,
                    error: "",
                  },
                });
              }}
              required
              error={isTruthy(formFields.name.error)}
              inputProps={{ minLength: 1, maxLength: 90 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <span style={{ fontSize: "0.75rem" }}>
                      {formFields.name?.value?.length} / 90
                    </span>
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <Tooltip title={submitCampaign.campaign.name} arrow>
              <Typography
                variant="h4"
                noWrap
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                maxWidth="250px"
                display="block"
              >
                {submitCampaign.campaign.name}
              </Typography>
            </Tooltip>
          )}
          {editName ? (
            <>
              <Tooltip title="Save" arrow>
                <IconButton onClick={saveCampaignName}>
                  <SvgIcon>
                    <CheckIcon fontSize="small" />
                  </SvgIcon>
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel" arrow>
                <IconButton onClick={() => setEditName(false)}>
                  <SvgIcon>
                    <CloseIcon fontSize="small" />
                  </SvgIcon>
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Edit" arrow>
              <IconButton
                onClick={() => {
                  setFormFields({
                    ...formFields,
                    name: {
                      error: "",
                      value: submitCampaign.campaign.name,
                    },
                  });
                  setEditName(true);
                }}
              >
                <SvgIcon>
                  <ModeEditIcon fontSize="small" />
                </SvgIcon>
              </IconButton>
            </Tooltip>
          )}
          <Chip label={submitCampaign.campaign.status} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            disabled={
              !submitCampaign.campaign.fromIdEmail ||
              !submitCampaign.campaign.subject ||
              !submitCampaign.campaignData.domainName ||
              !submitCampaign.campaign.content
            }
            onClick={() => setIsTestModalOpen(true)}
          >
            Send test email
          </Button>
          {checkMessageIdForUpdateData ? (
            <Button
              variant="contained"
              onClick={() => handleSubmission(submitCampaign, scheduleTime)}
            >
              Update
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={
                submitCampaign.campaignSegments.segmentIds.length === 0 ||
                !submitCampaign.campaign.fromIdEmail ||
                !submitCampaign.campaignData.domainName ||
                !submitCampaign.campaign.subject ||
                !submitCampaign.campaign.content ||
                !submitCampaign.campaignData.domainName
              }
              onClick={() => setSchedule(true)}
            >
              Schedule
            </Button>
          )}
        </Stack>
      </Grid>
    );
  };

  const getBody = () => {
    return (
      <Card>
        <Stack spacing={4} py={4} divider={<Divider />}>
          <FromSection />
          <RecipientsSection />
          <SubjectSection />
          <DesignSection
            setIsDesignEmailModalOpen={setIsDesignEmailModalOpen}
            design={design}
          />
          {checkMessageIdForUpdateData && <ScheduleSection />}
          <AttachmentSection />
        </Stack>
      </Card>
    );
  };

  const doneEditing = async (content: any) => {
    setSaving(true);
    const campaign = {
      ...submitCampaign,
      campaign: {
        ...submitCampaign.campaign,
        content: content.content,
        contentJson: content.contentJson,
        contentPlain: content.contentPlain,
      },
    };
    setDesign(content);
    setIsDesignEmailModalOpen(false);
    setSubmitCampaign(campaign);
    await saveAsDraft(campaign);
    setSaving(false);
  };

  const getDesignEmailModal = () => {
    return (
      <DesignEmail
        handleDialogClose={() => setIsDesignEmailModalOpen(false)}
        isDialogOpen={isDesignEmailModalOpen}
        doneEditing={doneEditing}
        design={design}
        setDesign={setDesign}
      />
    );
  };

  const getTestModal = () => {
    return (
      <TestModal
        handleDialogClose={() => setIsTestModalOpen(false)}
        isDialogOpen={isTestModalOpen}
      />
    );
  };

  const getScheduleModal = () => {
    return (
      <Schedule
        handleDialogClose={() => setSchedule(false)}
        isDialogOpen={schedule}
        handleSubmission={handleSubmission}
      />
    );
  };

  const getPage = () => {
    return (
      <Stack spacing={4}>
        {getHeader()}
        {getBody()}
      </Stack>
    );
  };

  return (
    <>
      {getPage()}
      {getDesignEmailModal()}
      {getScheduleModal()}
      {getTestModal()}
      <CustomLoader isLoading={saving} />
    </>
  );
};

export default Step2;
